"use client";

import clsx from "clsx";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { AcfText, SectionProps } from "@/components/page-builder/types";
import WpImage from "@/components/ui/WpImage";
import { themeImage, type MaybeMedia } from "@/lib/media";
import { filled, list, richText } from "./text";

type Tab = {
  /** Icon name, one of the theme's resources/images/icons/<ico>.svg. */
  ico?: AcfText;
  label?: AcfText;
  text?: AcfText;
  media_type?: "cover" | "contain" | "video" | "macbook" | string | false;
  image?: MaybeMedia;
  video?: MaybeMedia;
};

type Fields = {
  /** Plain text; *stars* highlight a phrase, new lines break. */
  title?: AcfText;
  text?: AcfText;
  tabs?: Tab[] | false;
};

// Video and cover images fill the frame.
const FILL = "absolute top-0 left-0 size-full object-cover";

// Ships with the theme; the screen insets in the SCSS are measured against it.
const MACBOOK_VIDEO = themeImage("das-academies/das-macbook-mobile-ui.mp4");

/**
 * DAS Academies How It Works, with the port of das-how-it-works.js.
 *
 * A 16:9 stage that cross-fades between one panel per tab, with a pill tab bar
 * underneath. Each tab picks how its media is treated:
 *
 *   cover   image filling the frame
 *   contain image sitting inside the frame
 *   video   looping video filling the frame
 *   macbook the theme's laptop animation, with the image dropped onto its screen
 *
 * The macbook clip is ours rather than the author's: the screen container is
 * pinned to where the screen lands in the last frame, which only holds for that
 * one file. So the author picks the image and nothing else.
 *
 * Every panel is in the markup and swapped by data-active, so switching tab never
 * waits on a download. Only the visible panel's video is loaded and playing.
 *
 * The macbook panel's video is a one-shot clip rather than a loop, so it is
 * rewound each time its tab is chosen. Its screen image only appears once the
 * clip has run out and the laptop has come to rest — before that the screen is
 * still moving and a pinned image would slide out from under it.
 */
export default function HowItWorks({
  title,
  text,
  tabs,
  section_id,
}: SectionProps<Fields>) {
  const rows = list(tabs);
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState(0);

  // Bumped on every selection, including of the tab already active, since
  // each one rewinds its video. 0 until the section has first been seen.
  const [run, setRun] = useState(0);

  // The panel whose one-shot clip has ended.
  const [settled, setSettled] = useState<number | null>(null);

  /** Show one tab's panel and description, and start only its video. */
  const select = (index: number) => {
    setActive(index);
    setRun((count) => count + 1);

    // Rewinding replays the clip, so the screen has to go back too.
    setSettled(null);
  };

  // Hold off until the section is on screen so the first video is not fetched early.
  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRun((count) => count + 1);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          setRun((count) => count + 1);
          setSettled(null);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const stage = stageRef.current;

    if (!stage || !run) {
      return;
    }

    stage
      .querySelectorAll<HTMLElement>(":scope > [data-tab]")
      .forEach((panel) => {
        const isActive = panel.getAttribute("data-tab") === String(active);

        panel.querySelectorAll("video").forEach((video) => {
          if (!isActive) {
            video.pause();

            return;
          }

          const src = video.getAttribute("data-src");

          if (src && video.getAttribute("src") !== src) {
            video.setAttribute("src", src);
            video.load();
          }

          video.currentTime = 0;
          video.play()?.catch(() => {});
        });
      });
  }, [run, active]);

  if (!rows.length) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="py-24 bg-das-light tablet:py-18 mobile:py-14 js-das-how-it-works"
      data-id={section_id || undefined}
    >
      <div className="shell">
        {(filled(title) || filled(text)) && (
          <div className="max-w-[720px] mx-auto text-center js-das-reveal">
            {filled(title) && (
              <h2 className="mb-0! font-das text-[44px] leading-[1.08] font-bold tracking-[-0.02em] text-das-black mobile:text-[34px]">
                {richText(String(title), "text-das-green")}
              </h2>
            )}

            {filled(text) && (
              <p className="max-w-[52ch] mx-auto mt-3 mb-0 font-das text-[17px] leading-[1.5] font-normal text-das-gray mobile:text-[15px]">
                {richText(String(text), "text-das-green")}
              </p>
            )}
          </div>
        )}

        {/* Panels are stacked and cross-faded rather than swapped, so the stage never collapses between tabs. */}
        <div
          ref={stageRef}
          className="relative overflow-hidden w-full max-w-[960px] min-[1536px]:max-w-[1300px] mx-auto mt-10 aspect-video rounded-[20px] bg-das-light mobile:mt-7 mobile:mx-[calc(50%-50vw)] mobile:max-w-none mobile:rounded-none"
        >
          {rows.map((tab, index) => {
            const mediaType = tab.media_type || "cover";
            const image = tab.image || false;
            const video = tab.video || false;
            const alt = tab.label ? String(tab.label) : "";

            return (
              <div
                key={index}
                className="absolute top-0 left-0 size-full bg-das-light opacity-0 invisible transition-[opacity,visibility] duration-500 ease-[ease] data-[active=true]:opacity-100 data-[active=true]:visible"
                data-tab={index}
                data-active={index === active}
              >
                {mediaType === "macbook" ? (
                  <>
                    {/* Only the one-shot clip ever ends; the looping ones never fire this. */}
                    <video
                      className={FILL}
                      muted
                      playsInline
                      preload="none"
                      data-src={MACBOOK_VIDEO}
                      onEnded={() => setSettled(index)}
                    ></video>

                    {/*
                     * The image dropped onto the laptop screen. The insets are
                     * percentages of the frame, measured against the clip's last
                     * frame, so the screen holds its place as the stage resizes.
                     */}
                    {image && (
                      <div
                        className={clsx(
                          "absolute top-[13.1%] right-[20.1%] bottom-[12.5%] left-[20%] overflow-hidden rounded-[4px] bg-black transition-opacity duration-450 ease-[ease]",
                          index === settled ? "opacity-100" : "opacity-0",
                        )}
                      >
                        <WpImage
                          media={image}
                          className="block size-full object-cover"
                          alt={alt}
                          sizes="(max-width: 767px) 60vw, 600px"
                        />
                      </div>
                    )}
                  </>
                ) : mediaType === "video" ? (
                  video && (
                    <video
                      className={FILL}
                      muted
                      loop
                      playsInline
                      preload="none"
                      data-src={video.src}
                    ></video>
                  )
                ) : (
                  image && (
                    <WpImage
                      media={image}
                      // Contain: the artwork sits inside the frame rather than filling it.
                      className={
                        mediaType === "contain"
                          ? "absolute top-1/2 left-1/2 w-[90%] h-[90%] -translate-1/2 object-contain"
                          : FILL
                      }
                      alt={alt}
                      sizes="(max-width: 1200px) 100vw, 1200px"
                    />
                  )
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-center mt-6 mobile:mt-5">
          <ul className="flex items-stretch m-0 p-1.5 rounded-[22px] bg-brand-blue list-none mobile:w-full">
            {rows.map((tab, index) => {
              const isActive = index === active;

              return (
                <li key={index} className="m-0 [&+&]:ml-1 mobile:w-1/4">
                  <button
                    type="button"
                    className={clsx(
                      "flex flex-col items-center justify-center w-full h-full py-3 px-6 border-0 rounded-[16px] text-center cursor-pointer appearance-none transition-[background-color,color] duration-300 ease-[ease] mobile:py-2.5 mobile:px-1.5",
                      isActive
                        ? "bg-white text-das-green hover:bg-white"
                        : "bg-transparent text-white/70 hover:bg-white/5",
                    )}
                    data-tab={index}
                    data-active={isActive}
                    onClick={() => select(index)}
                  >
                    {/* Painted through a mask, so one white SVG takes the green / white colouring. */}
                    {filled(tab.ico) && (
                      <i
                        className="block size-5 mb-1 bg-current mask-(--tab-ico-url) mask-no-repeat mask-center mask-contain"
                        style={
                          {
                            "--tab-ico-url": `url('${themeImage(`icons/${tab.ico}.svg`)}')`,
                          } as CSSProperties
                        }
                      ></i>
                    )}

                    {filled(tab.label) && (
                      <span
                        className={clsx(
                          "font-das text-[13px] leading-[1.2] font-medium transition-[color] duration-300 ease-[ease] mobile:text-[10px]",
                          isActive ? "text-das-black" : "text-white/70",
                        )}
                      >
                        {tab.label}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Every description shares one grid cell, so the block takes the height of the tallest and nothing below it shifts. */}
        <div className="grid max-w-[46ch] mx-auto mt-8 text-center">
          {rows.map((tab, index) =>
            filled(tab.text) ? (
              <p
                key={index}
                className="col-start-1 row-start-1 font-das text-[21px] leading-[1.5] font-normal text-das-gray opacity-0 invisible transition-[opacity,visibility] duration-350 ease-[ease] data-[active=true]:opacity-100 data-[active=true]:visible mobile:text-[18px]"
                data-tab={index}
                data-active={index === active}
              >
                {tab.text}
              </p>
            ) : null,
          )}
        </div>
      </div>
    </section>
  );
}
